import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import Layout from './Layout';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  displayOrder: number;
}

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses, isLightTheme } = useTheme();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/faq');
      const sortedFaqs = (response.data.faqs || []).sort((a: FAQItem, b: FAQItem) => 
        a.displayOrder - b.displayOrder
      );
      setFaqs(sortedFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load frequently asked questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (id: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Layout title="Frequent Questions" showBackButton onBack={() => navigate('/')}>
        <div className="p-4">
          <div className={`${themeClasses.card} p-8 text-center rounded-lg`}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className={`${themeClasses.textSecondary} mt-4`}>Loading FAQs...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Frequent Questions" showBackButton onBack={() => navigate('/')}>

      {/* Content */}
      <div className="p-4">
        <div className={`${themeClasses.card} rounded-lg`}>
          <div className="flex items-center mb-6">
            <MessageSquare className={`w-5 h-5 ${themeClasses.textSecondary} mr-2`} />
            <h2 className={`text-lg font-semibold ${themeClasses.text}`}>Frequently Asked Questions</h2>
          </div>
          
          {faqs.length === 0 ? (
            <div className={`text-center py-12 ${themeClasses.textSecondary}`}>
              <p>No frequently asked questions available yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`${themeClasses.border} border rounded-lg overflow-hidden transition-all`}
                >
                  <button
                    onClick={() => toggleItem(faq.id)}
                    className={`w-full text-left px-4 py-3 ${themeClasses.text} hover:${isLightTheme ? 'bg-gray-50' : 'bg-gray-800'} flex items-center justify-between transition-colors`}
                  >
                    <span className="font-medium pr-4">{faq.question}</span>
                    {expandedItems.has(faq.id) ? (
                      <ChevronUp className={`w-5 h-5 ${themeClasses.textSecondary} flex-shrink-0`} />
                    ) : (
                      <ChevronDown className={`w-5 h-5 ${themeClasses.textSecondary} flex-shrink-0`} />
                    )}
                  </button>
                  {expandedItems.has(faq.id) && (
                    <div className={`px-4 py-3 ${isLightTheme ? 'bg-gray-50' : 'bg-gray-800'} border-t ${themeClasses.border}`}>
                      <div className="prose prose-sm max-w-none">
                        <MarkdownRenderer content={faq.answer} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FAQ;

